import React, { useState, useEffect } from "react";
import "./robotSiteInfo.scss";
import PlantHeader from "../plantHeader/plantHeader";
import Menu from "../menu/menu";
import Search from "../search/search";
import download from "./download-icon.svg";
import upload from "./upload-icon.svg";
import Loader from "../loader/loader";
import EditRobotMoreSettings from "../editRobotMoreSettings/editRobotMoreSettings";
import { useNavigate } from "react-router-dom";
import ApiHandler from "../api";
import { getCurrentProject } from "../../utils/projectUtils";

const RobotSiteInfo = () => {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [robotSettings, setRobotSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditRobotMoreSettings, setShowEditRobotMoreSettings] =
    useState(false);
  const [selectedRobot, setSelectedRobot] = useState(null);
  const [sortParam, setSortParam] = useState("rowNumber");
  const [sortDirection, setSortDirection] = useState(1);

  const apiHandler = new ApiHandler();

  useEffect(() => {
    const storedProject = getCurrentProject();
    if (!storedProject) {
      navigate("/projects");
      return;
    }
    setProject(storedProject);
  }, [navigate]);

  useEffect(() => {
    if (project?._id) {
      fetchRobotSettings();
    }
  }, [project]);

  const onRobotUpdated = (robotData) => {
    fetchRobotSettings();
  };

  const fetchRobotSettings = async (search = "") => {
    setIsLoading(true);
    try {
      const response = await apiHandler.getRobotMoreSettings(
        project._id,
        search
      );
      setRobotSettings(response || []);
    } catch (error) {
      console.error("Error fetching robot settings:", error);
      alert("Failed to load robot settings");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      setIsLoading(true);
      const response = await apiHandler.exportRobotMoreSettings(project._id);
      const element = document.createElement("a");
      const file = new Blob([response.data], { type: "text/plain" });
      element.href = URL.createObjectURL(file);
      element.download = `${project.projectName} robot site info.csv`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      alert("Failed to download CSV");
    } finally {
      setIsLoading(false);
    }
  };

  const searchClicked = (search) => {
    fetchRobotSettings(search);
  };

  const fileUploadClick = () => {
    document.getElementById("fileUpload").click();
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("projectId", project._id);

    try {
      setIsLoading(true);
      await apiHandler.importRobotMoreSettings(formData);
      alert("File uploaded successfully");
      fetchRobotSettings();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    } finally {
      setIsLoading(false);
      event.target.value = "";
    }
  };

  const editRobotSettings = (selectedRobot) => {
    setSelectedRobot(selectedRobot);
    setShowEditRobotMoreSettings(true);
  };

  const closeEditRobotMoreSettings = () => {
    setShowEditRobotMoreSettings(false);
    setSelectedRobot(null);
  };

  const getBooleanValue = (val) =>
    val === "" || val === undefined
      ? ""
      : val === true || val === "yes"
      ? "Yes"
      : val === false || val === "no"
      ? "No"
      : val;

  const getHeaderTH = (paramName, text, className = "", type = "") => {
    const currentSort = sortParam === paramName;
    const direction = currentSort && sortDirection === 1 ? "asc" : "desc";

    return (
      <th
        key={paramName}
        className={`${className} ${currentSort ? direction : ""}`}
        onClick={() => handleSort(paramName)}
      >
        {text}
      </th>
    );
  };

  const handleSort = (paramName) => {
    if (sortParam === paramName) {
      setSortDirection(sortDirection * -1);
    } else {
      setSortParam(paramName);
      setSortDirection(1);
    }
  };

  if (!project) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page">
      {isLoading && <Loader />}

      {showEditRobotMoreSettings && (
        <EditRobotMoreSettings
          closeModal={closeEditRobotMoreSettings}
          robot={selectedRobot}
          onRobotUpdated={onRobotUpdated}
        />
      )}

      <div className="menu-wrapper">
        <Menu />
      </div>
      <div className="content">
        <PlantHeader
          title={"Robots & Site Info"}
          secondaryText={project.projectName}
        />
        <div className="robotSettings">
          <div className="top">
            <Search
              onSearch={searchClicked}
              placeholder="search by robot number"
            />
            <div className="buttons">
              <div className="button">
                <img src={download} alt="download" />
                <span className="text" onClick={downloadCsv}>
                  EXPORT
                </span>
              </div>

              <div className="button" onClick={fileUploadClick}>
                <img src={upload} alt="upload" />
                <span className="text">UPLOAD CSV</span>
                <input
                  id="fileUpload"
                  type="file"
                  accept=".csv"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                {getHeaderTH("robot", "Robot")}
                {getHeaderTH("rowNumber", "Row Number")}
                {getHeaderTH("length", "Row Length [m]")}
                {getHeaderTH("serialNumber", "Serial Number")}
                {getHeaderTH("pcbNumber", "PCB Version")}
                {getHeaderTH("swVersion", "Software Version")}
                {getHeaderTH("flippers", "Flippers", "", "boolean")}
                {getHeaderTH(
                  "wheelExtension",
                  "Wheel Extension",
                  "",
                  "boolean"
                )}
                {getHeaderTH("fourByFour", "4 X 4", "", "boolean")}
                {getHeaderTH("robotVersion", "Robot version")}
                {getHeaderTH("numberOfPanels", "Panel size")}
                {getHeaderTH("bridges", "Bridges")}
                {getHeaderTH("integralPanel", "Integral Panel")}
                {getHeaderTH("OCM", "OCM")}
                {getHeaderTH("dockingStation", "Docking Station")}
                {getHeaderTH("batterySize", "Battery Size")}
                {getHeaderTH("freeText", "Free Text")}
              </tr>
            </thead>
            <tbody>
              {robotSettings.map((robotSetting, index) => (
                <tr
                  key={index}
                  onClick={() => editRobotSettings(robotSetting)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    {robotSetting.robotName}
                    {robotSetting.rpi ? ` (${robotSetting.rpi})` : ""}
                  </td>
                  <td>{robotSetting.moreSettings?.rowNumber}</td>
                  <td>{robotSetting.moreSettings?.rowLength}</td>
                  <td>{robotSetting.moreSettings?.serialNumber}</td>
                  <td>{robotSetting.moreSettings?.pcbNumber}</td>
                  <td>{robotSetting.moreSettings?.swVersion}</td>
                  <td className="boolean">
                    {getBooleanValue(robotSetting.moreSettings?.flippers)}
                  </td>
                  <td className="boolean">
                    {getBooleanValue(robotSetting.moreSettings?.wheelExtension)}
                  </td>
                  <td className="boolean">
                    {getBooleanValue(robotSetting.moreSettings?.fourByFour)}
                  </td>
                  <td>{robotSetting.moreSettings?.robotVersion}</td>
                  <td>{robotSetting.moreSettings?.numberOfPanels}</td>
                  <td>{getBooleanValue(robotSetting.moreSettings?.bridges)}</td>
                  <td>
                    {getBooleanValue(robotSetting.moreSettings?.integralPanel)}
                  </td>
                  <td>{getBooleanValue(robotSetting.moreSettings?.OCM)}</td>
                  <td>
                    {getBooleanValue(robotSetting.moreSettings?.dockingStation)}
                  </td>
                  <td>{robotSetting.moreSettings?.batterySize}</td>
                  <td>{robotSetting.moreSettings?.freeText}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RobotSiteInfo;
